import { PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import { globalActions } from 'app/containers/Global/slice';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import taskApi from 'app/containers/Task/taskApi';
import i18next from 'i18next';
import { put, select, takeLatest } from 'redux-saga/effects';
import { resourceActions as actions } from '.';
import resourceApi from '../resourceApi';
import {
  selectSelectedTaskCategoryId,
  selectSelectedUsedResourceId,
} from './selectors';
import {
  IGetResourcesByTaskCategoryIdPayload,
  IGetUsedResourceDistributionPayload,
  IResourceByTaskCategory,
  IResourceDistributionListItem,
  IResourceType,
  ISubmitUsedResourcesUpdatesPayload,
  IGetHarvestPayload,
  IGetHarvestByCultureAndCompanyPayload,
  IHarvestListItem,
  IHarvestByCultureAndCompanyListItem,
} from './types';

function* loadResourceTypesSaga() {
  try {
    yield put(actions.setResourceTypesLoading(true));

    const types: IResourceType[] = yield resourceApi.getAllResourceTypes();

    yield put(actions.setResourceTypes(types));
  } catch (err) {
    yield put(globalActions.handleError(err));
    yield put(actions.setResourceTypes([]));
  } finally {
    yield put(actions.setResourceTypesLoading(false));
  }
}

function* getResourcesByTaskCategoryIdSaga(
  action: PayloadAction<IGetResourcesByTaskCategoryIdPayload>,
) {
  try {
    yield put(actions.setResourcesByTaskCategoryIdLoading(true));

    const {
      envId,
      taskCategoryId,
      companyId,
      companyGroupId,
      filters,
      seasonId,
    } = action.payload;

    const resources: IResourceByTaskCategory[] = yield resourceApi.getUsedResourcesDistribution(
      { envId, taskCategoryId, companyId, companyGroupId, filters, seasonId },
    );

    yield put(actions.setResourcesByTaskCategoryId(resources));
  } catch (err) {
    yield put(globalActions.handleError(err));
    yield put(actions.setResourcesByTaskCategoryId([]));
  } finally {
    yield put(actions.setResourcesByTaskCategoryIdLoading(false));
  }
}

function* getUsedResourceDistributionSaga(
  action: PayloadAction<IGetUsedResourceDistributionPayload>,
) {
  try {
    yield put(actions.setUsedResourceDistributionLoading(true));
    const {
      seasonId,
      envId,
      resourceId,
      companyId,
      companyGroupId,
      filters,
      taskCategoryId,
    } = action.payload;

    const response: IResourceDistributionListItem[] = yield resourceApi.getUsedResourcesDistributionByResource(
      {
        seasonId,
        envId,
        resourceId,
        companyId,
        companyGroupId,
        filters,
        taskCategoryId,
      },
    );

    yield put(actions.setUsedResourceDistribution(response));
  } catch (err) {
    yield put(globalActions.handleError(err));
    yield put(actions.setUsedResourceDistribution([]));
  } finally {
    yield put(actions.setUsedResourceDistributionLoading(false));
  }
}

function* submitUsedResourcesUpdatesSaga(
  action: PayloadAction<ISubmitUsedResourcesUpdatesPayload>,
) {
  try {
    const { envId, usedResourcesArr, filters } = action.payload;
    if (!usedResourcesArr.length) {
      return;
    }
    yield put(actions.setUsedResourcesUpdatesSubmitLoading(true));
    yield taskApi.addTaskUsedResource(usedResourcesArr, envId);

    const usedResourceId = yield select(selectSelectedUsedResourceId);
    const companyId = yield select(selectSelectedCompanyId);
    const companyGroupId = yield select(selectSelectedCompanyGroupId);
    const selectedCategoryId = yield select(selectSelectedTaskCategoryId);
    const seasonId = yield select(selectSelectedSeasonId);

    // reload - Distribution Card
    yield put(
      actions.getUsedResourceDistribution({
        seasonId,
        envId,
        resourceId: usedResourceId,
        companyId,
        companyGroupId,
        filters,
        taskCategoryId: selectedCategoryId,
      }),
    );
    // reload - Resource By Category
    yield put(
      actions.getResourcesByTaskCategoryId({
        envId,
        taskCategoryId: selectedCategoryId,
        companyId,
        companyGroupId,
        filters,
        seasonId,
      }),
    );

    notification.success({
      message: i18next?.t(
        'Resource.Notification.UsedResourcesSaved',
        'Used resources saved successfully',
      ),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setUsedResourcesUpdatesSubmitLoading(false));
  }
}

function* getHarvestSaga(action: PayloadAction<IGetHarvestPayload>) {
  try {
    yield put(actions.setHarvestLoading(true));
    const { envId, companyId, companyGroupId } = action.payload;

    const harvestArr: IHarvestListItem[] = yield resourceApi.getYieldSum({
      envId,
      companyId,
      companyGroupId,
    });

    yield put(actions.setHarvest(harvestArr));
  } catch (err) {
    yield put(globalActions.handleError(err));
    yield put(actions.setHarvest([]));
  } finally {
    yield put(actions.setHarvestLoading(false));
  }
}

function* getHarvestByCultureAndCompanySaga(
  action: PayloadAction<IGetHarvestByCultureAndCompanyPayload>,
) {
  try {
    yield put(actions.setHarvestByCultureAndCompanyLoading(true));
    const { envId, companyId, cultureId, companyGroupId } = action.payload;

    const harvestArr: IHarvestByCultureAndCompanyListItem[] = yield resourceApi.getYieldByCultureAndCompany(
      {
        envId,
        cultureId,
        companyId,
        companyGroupId,
      },
    );

    yield put(actions.setHarvestByCultureAndCompany(harvestArr));
  } catch (err) {
    yield put(globalActions.handleError(err));
    yield put(actions.setHarvest([]));
  } finally {
    yield put(actions.setHarvestByCultureAndCompanyLoading(false));
  }
}

export function* resourceSaga() {
  yield takeLatest(
    actions.getHarvestByCultureAndCompany.type,
    getHarvestByCultureAndCompanySaga,
  );

  yield takeLatest(actions.getHarvest.type, getHarvestSaga);
  yield takeLatest(
    actions.submitUsedResourcesUpdates.type,
    submitUsedResourcesUpdatesSaga,
  );

  yield takeLatest(
    actions.getUsedResourceDistribution.type,
    getUsedResourceDistributionSaga,
  );

  yield takeLatest(
    actions.getResourcesByTaskCategoryId.type,
    getResourcesByTaskCategoryIdSaga,
  );

  yield takeLatest(actions.loadResourceTypes.type, loadResourceTypesSaga);
}
